import { FilterableMultiSelect, Layer } from '@carbon/react';

type ListBoxBaseItemType = {
  id: string;
  label?: string;
  [key: string]: unknown;
};
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface SearchableMultiSelectProps<T extends ListBoxBaseItemType> {
  id: string;
  title: string;
  placeholder: string;
  items: T[];
  selectedItems: T[];
  onSelectionChange: (selectedItems: T[]) => void;
  onSearch: (searchText: string) => void;
  itemToString: (item: T) => string;
  searchDebounceMs?: number;
  light?: boolean;
  className?: string;
}

function SearchableMultiSelect<T extends ListBoxBaseItemType>({
  id,
  title,
  placeholder,
  items,
  selectedItems,
  onSelectionChange,
  onSearch,
  itemToString,
  searchDebounceMs = 300,
  light = true,
  className,
}: SearchableMultiSelectProps<T>) {
  const [searchText, setSearchText] = useState('');

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        onSearch(searchValue);
      }, searchDebounceMs),
    [onSearch, searchDebounceMs]
  );

  // Cleanup debounced function
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle selection change
  const handleSelectionChange = useCallback(
    ({ selectedItems: newSelectedItems }: { selectedItems: T[] }) => {
      onSelectionChange(newSelectedItems);

      // Trigger search when all selections are cleared
      if (newSelectedItems.length === 0) {
        debouncedSearch('');
      }
    },
    [onSelectionChange, debouncedSearch]
  );

  // Handle filter items (search input)
  const handleFilterItems = useCallback(
    (
      items: readonly T[],
      { inputValue }: { inputValue: string | null }
    ): T[] => {
      // Only update search text and trigger search if input actually changed
      if (inputValue !== searchText) {
        setSearchText(inputValue || '');
        // Always trigger search, even when clearing (empty string)
        debouncedSearch(inputValue || '');
      }
      // Return all items as mutable array - let Carbon's FilterableMultiSelect handle the filtering
      return Array.from(items);
    },
    [searchText, debouncedSearch]
  );

  const multiSelect = (
    <FilterableMultiSelect
      key={`${id}-${selectedItems.length}`}
      id={id}
      titleText=""
      placeholder={placeholder}
      items={items}
      itemToString={itemToString}
      initialSelectedItems={selectedItems}
      onChange={handleSelectionChange}
      filterItems={handleFilterItems}
    />
  );

  return (
    <div className={className}>
      <h4>{title}</h4>
      {light ? <Layer>{multiSelect}</Layer> : multiSelect}
    </div>
  );
}

export default SearchableMultiSelect;
